import type { WorksheetAssignScheduleStatus } from '@mathflat/domain/Worksheet'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'

import Button from '@/component/atoms/Button/Button/Button'
import { Loader } from '@/component/atoms/Loader'
import Chip from '@/component/molecules/Chip/Chip'
import { Radio } from '@/component/molecules/Radio'
import ModalTemplate from '@/component/templates/ModalTemplate'
import useInfiniteScroll from '@/hooks/useInfiniteScroll'

import ScheduleCard from './_components/ScheduleCard'
import { SCHEDULE_LIST_STRINGS, SCHEDULE_STATUSES } from './_constants/scheduleStrings'
import { ScheduleListService } from './_service/ScheduleList.service'
import { formatTabCount } from './_utils/scheduleListFormat'
import S from './ScheduleListView.style'

type Status = WorksheetAssignScheduleStatus

type Props = {
  onBack: () => void
}

/** 출제 모달 안에서 "예약 목록 보기"로 들어오는 학원 단위 예약 목록. 모달 크기는 출제 모달과 같다 */
const ScheduleListView = observer(({ onBack }: Props) => {
  const [list] = useState(() => new ScheduleListService())
  useEffect(() => {
    void list.load()
  }, [list])

  const sentinelRef = useRef<HTMLDivElement>(null)
  useInfiniteScroll(sentinelRef, list.loadMore)

  return (
    <ModalTemplate
      css={S.modal}
      header={
        <header css={S.header}>
          <h3 className="title">{SCHEDULE_LIST_STRINGS.title()}</h3>
          <sub>{SCHEDULE_LIST_STRINGS.subtitle}</sub>
        </header>
      }
      body={
        <div css={S.body}>
          <div className="tabs-row">
            <Radio.Group
              value={list.status}
              onChange={(e) => list.setStatus(e.target.value as Status)}
              className="tabs"
            >
              {SCHEDULE_STATUSES.map((value) => (
                <Chip.Choice key={value} value={value} css={S.chip}>
                  {SCHEDULE_LIST_STRINGS.tab(value)}
                </Chip.Choice>
              ))}
            </Radio.Group>
            {list.counts && (
              <span className="count">{formatTabCount(list.status, list.counts)}</span>
            )}
          </div>
          <div className="list" role="list" aria-label={SCHEDULE_LIST_STRINGS.tab(list.status)}>
            {list.loadState === 'loading' ? (
              <div className="center">
                <Loader />
              </div>
            ) : list.loadState === 'error' ? (
              <div className="center error">
                <p>{SCHEDULE_LIST_STRINGS.loadFailed}</p>
                <Button size="s" variant="outline" color="neutral" onClick={list.load}>
                  {SCHEDULE_LIST_STRINGS.retry}
                </Button>
              </div>
            ) : list.items.length === 0 ? (
              <p className="center empty">{SCHEDULE_LIST_STRINGS.empty}</p>
            ) : (
              list.items.map((detail) => (
                <div key={detail.batchId} role="listitem">
                  <ScheduleCard
                    detail={detail}
                    status={list.status}
                    onCancel={
                      list.status === 'WAITING' ? () => list.cancel(detail.batchId) : undefined
                    }
                  />
                </div>
              ))
            )}
            {list.isLoadingMore && (
              <div className="center">
                <Loader />
              </div>
            )}
            <div ref={sentinelRef} className="sentinel" />
          </div>
        </div>
      }
      footer={
        <footer css={S.footer}>
          <Button width={120} size="l" variant="line-filled" color="neutral" onClick={onBack}>
            {SCHEDULE_LIST_STRINGS.back}
          </Button>
        </footer>
      }
    />
  )
})

export default ScheduleListView
